import clsx from 'clsx';

//types
import type { SocialProfileNetworkType } from '@/types/socialProfileType';
import type { AlbumCardGridLayoutType } from '@/lib/albums/types';

//lib
import {
    FACEBOOK,
    INSTAGRAM,
    LINKEDIN,
    PINTEREST,
    TIKTOK_BUSINESS,
    TWITTER,
    YOUTUBE
} from '@/lib/socialProfile';

function instagramDefaultGrid({
    index,
    size,
    gridLayout
}: Pick<CardGridStyleProps, 'index' | 'size' | 'gridLayout'>) {
    if (gridLayout === 'table') {
        return clsx('relative flex', {
            'col-span-full row-span-full': index === 1 && size === 1,
            'col-span-full row-span-6': (index === 1 && size > 1) || (index === 2 && size === 2),
            'col-span-6 row-span-6': index > 1 && size > 2
        });
    }

    return clsx('relative flex', {
        'col-span-full row-span-full': index === 1 && size === 1,
        'col-span-full row-span-9': index === 1 && size > 2,
        'col-span-full row-span-6': (index === 1 && size === 2) || (index === 2 && size === 2),
        'col-span-6 row-span-3': (index === 2 && size === 3) || (index === 3 && size === 3),
        'col-span-4 row-span-3': index >= 2 && size >= 4
    });
}

function defaultGrid({
    index,
    size,
    gridLayout
}: Pick<CardGridStyleProps, 'index' | 'size' | 'gridLayout'>) {
    if (gridLayout === 'table') {
        return clsx('relative flex', {
            'col-span-full row-span-full text-xs': index === 1 && size === 1,
            'col-span-full row-span-6 text-xs':
                (index === 1 && size > 1) || (index === 2 && size === 2),
            'col-span-6 row-span-6 text-9': index > 1 && size > 2
        });
    }
    return clsx('relative flex', {
        'col-span-full row-span-full text-3xl': index === 1 && size === 1,
        'col-span-full row-span-8 text-3xl': index === 1 && size === 3,
        'col-span-full row-span-6 text-3xl':
            (index === 1 && size === 2) || (index === 2 && size === 2),
        'col-span-6 row-span-4 text-2xl':
            (index === 2 && size === 3) || (index === 3 && size === 3),
        'col-span-6 row-span-6 text-2xl': size >= 4
    });
}

function storiesFormatGrid({ index, size }: Pick<CardGridStyleProps, 'index' | 'size'>) {
    return clsx('relative flex', {
        'col-start-3 col-span-5 row-span-full': index === 1 && size === 1,
        'col-start-1 col-span-7 row-span-full': index === 1 && size >= 3,
        'col-span-6 row-span-full': (index === 1 && size === 2) || (index === 2 && size === 2),
        'col-span-5 row-span-6': index >= 2 && size >= 3
    });
}

type CardGridStyleProps = {
    index: number;
    size: number;
    network?: SocialProfileNetworkType;
    mediaType?: 'stories' | 'posts';
    gridLayout: AlbumCardGridLayoutType;
};

export function getCardGridStyles({
    index,
    size,
    network,
    mediaType = 'posts',
    gridLayout
}: CardGridStyleProps) {
    switch (network) {
        case INSTAGRAM:
            if (mediaType === 'stories') {
                return storiesFormatGrid({ index, size });
            }
            return instagramDefaultGrid({ index, size, gridLayout });
        case FACEBOOK:
        case LINKEDIN:
        case PINTEREST:
        case TWITTER:
        case YOUTUBE:
            return defaultGrid({ index, size, gridLayout });
        case TIKTOK_BUSINESS:
            return storiesFormatGrid({ index, size });
        default:
            return instagramDefaultGrid({ index, size, gridLayout });
    }
}
